// Pagina storico rapportini portale — tabella con filtri data, download PDF.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, Download, Filter, X, Star } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Skeleton } from '../../components/ui/Skeleton'
import { Card } from '../../components/ui/Card'
import { portalApiCalls } from './portal.api'
import { formatDate, formatRelativeDate } from '../../lib/utils'

export function PortalReportsPage() {
  const navigate = useNavigate()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'reports', { dateFrom, dateTo, page }],
    queryFn: () =>
      portalApiCalls
        .getReports({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          page,
          pageSize: 20,
        })
        .then((r) => r.data),
    placeholderData: (prev) => prev,
  })

  const hasFilters = Boolean(dateFrom || dateTo)
  const resetFilters = () => {
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rapportini</h1>
        <p className="mt-1 text-sm text-gray-500">
          Storico dei rapportini di intervento completati — scaricabili in PDF
        </p>
      </div>

      {/* Filtri */}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" aria-hidden="true" />
            <span className="text-sm font-medium text-gray-700">Filtra per data:</span>
          </div>
          <div className="flex-1 min-w-[140px]">
            <Input
              label="Dal"
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
              max={dateTo || undefined}
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <Input
              label="Al"
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
              min={dateFrom || undefined}
            />
          </div>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              leftIcon={<X className="h-4 w-4" />}
            >
              Azzera
            </Button>
          )}
        </div>
      </Card>

      {/* Risultati */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-600">
          Impossibile caricare i rapportini. Riprova.
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} height={80} className="rounded-xl" />
          ))}
        </div>
      ) : data?.items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FileText className="mb-3 h-12 w-12 text-gray-200" aria-hidden="true" />
          <h3 className="text-base font-semibold text-gray-900">Nessun rapportino</h3>
          <p className="mt-1 text-sm text-gray-500">
            {hasFilters
              ? 'Nessun rapportino trovato con i filtri selezionati.'
              : 'I rapportini appariranno qui al termine degli interventi.'}
          </p>
          {hasFilters && (
            <button
              type="button"
              className="mt-3 text-sm text-[var(--fc-primary)] hover:underline"
              onClick={resetFilters}
            >
              Rimuovi filtri
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Info risultati */}
          {data && (
            <p className="text-sm text-gray-500">
              {data.total} rapportini trovati
            </p>
          )}

          {/* Lista rapportini */}
          <div className="space-y-3">
            {data?.items.map((report) => (
              <div
                key={report.id}
                className="flex items-start justify-between rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-start gap-4">
                  {/* Icona */}
                  <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gray-100">
                    <FileText className="h-5 w-5 text-gray-500" aria-hidden="true" />
                  </div>

                  {/* Dettagli */}
                  <div>
                    <p className="font-semibold text-gray-900">{report.reportNumber}</p>
                    {report.workDescription && (
                      <p className="mt-0.5 max-w-md text-sm text-gray-600 line-clamp-1">
                        {report.workDescription}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-3">
                      <span className="text-xs text-gray-400">
                        {formatDate(report.createdAt)}
                      </span>
                      <span className="text-xs text-gray-300">·</span>
                      <span className="text-xs text-gray-400">
                        {formatRelativeDate(report.createdAt)}
                      </span>
                      <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                        {report.status === 'sent' ? 'Inviato' : 'Completato'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Azioni */}
                <div className="ml-4 flex flex-shrink-0 items-center gap-2">
                  {/* Bottone valutazione */}
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 transition-colors hover:border-amber-300 hover:text-amber-600"
                    onClick={() => navigate(`/portal/rating?reportId=${report.id}`)}
                    aria-label={`Valuta rapportino ${report.reportNumber}`}
                  >
                    <Star className="h-3.5 w-3.5" aria-hidden="true" />
                    Valuta
                  </button>

                  {/* Download PDF */}
                  {report.pdfUrl ? (
                    <a
                      href={report.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 rounded-lg bg-[var(--fc-primary)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[var(--fc-primary-dark)]"
                      aria-label={`Scarica PDF rapportino ${report.reportNumber}`}
                    >
                      <Download className="h-3.5 w-3.5" aria-hidden="true" />
                      PDF
                    </a>
                  ) : (
                    <span className="rounded-lg border border-gray-100 px-3 py-1.5 text-xs text-gray-400">
                      PDF non pronto
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Paginazione */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Pagina {data.page} di {data.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Precedente
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                  disabled={page === data.totalPages}
                >
                  Successiva
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
